import { useMemo } from 'react';
import type { TextOnPathProps } from '../../TextOnPath.types';
import type { Measurement } from '../useSvgMeasurement';

type UseTextOnPathCalculationsProps = Pick<
  TextOnPathProps,
  'fillMode' | 'pathLength' | 'movementDirection' | 'speed' | 'text'
> & {
  measurement?: Measurement;
  startOffset?: number;
};

function getAnimationOffsets(
  movementDirection: TextOnPathProps['movementDirection'],
  biggestLength: number,
  startOffset = 0,
) {
  // If text is moving left-to-right
  if (movementDirection === 'forward') {
    return {
      from1: `-${biggestLength}`,
      to1: '0',
      from2: '0',
      to2: `${biggestLength + startOffset}`,
    };
  }

  // If text is moving right-to-left
  return {
    from1: '0',
    to1: `-${biggestLength}`,
    from2: `${biggestLength + startOffset}`,
    to2: '0',
  };
}

export function useTextOnPathCalculations({
  fillMode,
  pathLength,
  measurement,
  movementDirection,
  speed,
  text,
  startOffset = 0,
}: UseTextOnPathCalculationsProps) {
  const isHydrated = !!measurement;
  const isFillMode = fillMode === 'repeat';
  const isJustifyMode = fillMode === 'justify';

  // Calculate repetitions for fill mode using pathLength prop
  const fallbackRepetitions = isFillMode ? 10 : 1;
  const repetitions =
    isHydrated && isFillMode && pathLength && measurement?.textWidth
      ? Math.ceil(pathLength / measurement.textWidth)
      : fallbackRepetitions;

  const repeatedText = repetitions > 1 ? `${text} `.repeat(repetitions) : text;

  const { textWidthMultiplied, animationOffsets, duration } = useMemo(() => {
    let calculatedTextWidth: number | undefined;
    if (isJustifyMode) {
      calculatedTextWidth = pathLength;
    } else if (measurement?.textWidth) {
      calculatedTextWidth = measurement.textWidth * repetitions;
    }
    const calculatedLength = Math.max(
      calculatedTextWidth || 0,
      pathLength || 0,
    );
    const calculatedOffsets = getAnimationOffsets(
      movementDirection,
      calculatedLength,
      startOffset,
    );
    const distance = calculatedLength || 0;
    const calculatedDuration = speed === 0 ? -1 : distance / (speed * 10);

    return {
      textWidthMultiplied: calculatedTextWidth,
      animationOffsets: calculatedOffsets,
      duration: calculatedDuration,
    };
  }, [
    isJustifyMode,
    pathLength,
    measurement?.textWidth,
    repetitions,
    movementDirection,
    speed,
    startOffset,
  ]);

  return {
    textWidthMultiplied,
    animationOffsets,
    duration,
    repeatedText,
  };
}
